import React, { useState, useEffect, useRef } from 'react';
import '../view-archer-styles.css';

//material UI
import { Alert, Avatar, Box, Button, Chip, Collapse, Divider, Grid, Input, Paper, Stack, Tabs, Tab, TextField, Typography } from '@mui/material/';
import { blue} from '@mui/material/colors';
import { Close} from '@mui/icons-material/';

//third parties:
import { Editor } from '@tinymce/tinymce-react';

//components import
import AuditNoteCard from './AuditNoteCard';
import FlaggedItemsTable, { excessContributionCategoryId } from './FlaggedItemsTable';
import IssueAttachmentsDialog from './IssueAttachmentsDialog';
import ArcherIssue from '../dataObjects/ArcherIssue';
import FlaggedItemsEditor from './FlaggedItemsEditor';
import ArcherIssueGalleryTemplate from '../dataObjects/ArcherIssueGalleryTemplate';
import IssueReconcilationDialog from './IssueReconcilationDialog';
import IssueDataBindingDialog from './IssueDataBindingDialog';
import PlaceHolderItemDialog from './PlaceHolderItemDialog';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import FilerGatewayResponses from './FilerGatewayResponses';
import AuditNote from '../dataObjects/AuditNote';
import FlaggedItem from '../dataObjects/FlaggedItem';
import FlaggedItemGroup from '../dataObjects/FlaggedItemGroup';
import ArcherIssueDraft from '../dataObjects/ArcherIssueDraft';

interface IssueEditorProps {
    issueId: number;
    issueTemplates?: ArcherIssueGalleryTemplate[];
    closeIssueEditor: () => void,
}

interface TabPanelProps {
    children?: React.ReactNode;
    id: string;
    value: string;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, id, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== id}
            id={`letter-tabpanel-${id}`}
            aria-labelledby={`letter-tab-${id}`}
            {...other}
        >
            {value === id && (
                <Box sx={{ p: 3 }}>
                    <Grid>{children}</Grid>
                </Box>
            )}
        </div>
    );
}

function tabProps(id: string) {
    return {
        id: `letter-tab-${id}`,
        value: `${id}`,
        'aria-controls': `letter-tabpanel-${id}`,
    };
}  

const ArcherIssueEditor = (props: IssueEditorProps) => {

    //initial state set up
    const [issue, setIssue] = useState<ArcherIssue>();
    const [issueContent, setIssueContent] = useState('');
    const [issuePreviewContent, setPreivewContent] = useState('');
    const [issueHeader, setIssueHeader] = useState('');
    const [issueInstructions, setIssueInstructions] = useState('');
    const [issueTemplates, setIssueTemplates] = useState<ArcherIssueGalleryTemplate[]>();
    const [letterId, setLetterId] = useState(0);
    const [flaggedItems, setFlaggedItems] = useState<FlaggedItem[]>();

    //default tab
    const [tabValue, setTabValue] = useState('body');

    //Claude draft (Special 999): the pending draft, whether its preview is open, and a key that
    //remounts the header input and both TinyMCE editors after Accept. The header is an
    //uncontrolled defaultValue input and TinyMCE takes initialValue, so without a remount the old
    //text stays on screen and the next keystroke or mouse-leave saves it back over the draft.
    const [issueDraft, setIssueDraft] = useState<ArcherIssueDraft | null>(null);
    const [isDraftPreviewOpen, setDraftPreviewOpen] = useState(false);
    const [isDraftBusy, setDraftBusy] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const pendingSaves = useRef<Promise<any>[]>([]);

    useEffect(() => {
        getIssue();
        getPendingIssueDraft();
    }, [])

    const getIssue = async() => {
        let url = `/Archer3/GetIssue?issueId=${props.issueId}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetIssue',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    setIssue(data);
                    setIssueContent(data.body);
                    setIssueInstructions(data.instructions)
                    setIssueHeader(data.header);
                    setLetterId(data.initiaLetterId);
                    if (data.archerStatus !== 'Draft') {
                        getIssuePreviewContent();
                        setTabValue('responses');
                    }
                    if (data.flaggedItems.length > 0) {
                        setFlaggedItems(data.flaggedItems);
                    }

                    if (data.flaggedItemGroups.length>0) {
                        const items: FlaggedItem[] = [];
                        data.flaggedItemGroups.map((group: FlaggedItemGroup) => {
                            group.flaggedItems.map((item: FlaggedItem) => {
                                items.push(item)
                            })
                        });
                        setFlaggedItems(items);
                    }
                }
            })
    }

    /*TEXT EDITOR SET UP: LOAD TEMPLATES, INSERT TEMPLATE, CREATE TEMPLATE, DELETE TEMPLATE*/
    const editorAPIKey = process.env.REACT_APP_EDITOR_API_KEY;
    const editorBodyRef = useRef<any>(null);
    const editorInstructionRef = useRef<any>(null);

    /*Navigation*/
    const switchTab = (event: React.SyntheticEvent, newValue: string) => {

        if (newValue === "preview") {
            let issueBody = issueContent;
            if (editorBodyRef.current) {
                issueBody = editorBodyRef.current.getContent();
                if (issueBody.length > 0) 
                    setIssueContent(issueBody);
            }
            getIssuePreviewContent();
        }

        setTabValue(newValue);
    };


    const closeIssueEditor = () => {
        props.closeIssueEditor();
    }

    /*ISSUE*/
    const saveIssueHeader = (issueId: number, header: string) => {
        //The header saves on every keystroke and is required; skip the save while the box is
        //blank (mid-retype) instead of popping the "Please enter a header" error each time.
        if (header.trim() === '') {
            return;
        }
        var params = [
            `issueId=${issueId}`,
            `header=${encodeURIComponent(header)}`
        ].join('&');
        let url = `/Archer3/SaveIssueHeader?${params}`
        const save = fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveIssueHeader',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setIssueHeader(header);
                }
            })
        trackSave(save);
    }

    //Remembers an in-flight header/body/instructions save so Accept can wait for it: the mouse
    //leaving an editor on its way to the Accept button fires a save of the old text, and that
    //save must not land after the draft has been copied in.
    const trackSave = (save: Promise<any>) => {
        const settled = save.catch(() => { });
        pendingSaves.current.push(settled);
        settled.then(() => {
            pendingSaves.current = pendingSaves.current.filter(p => p !== settled);
        });
    }

    /* TEXT EDITOR */

    const saveIssueBody = () => {

        let issueBody = '';

        if (editorBodyRef.current) {
            issueBody = editorBodyRef.current.getContent();
        }

        const save = fetch('/Archer3/SaveIssueBody', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    issueId: props.issueId,
                    body: issueBody
                })
            }).then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveIssueBody',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    //setIssueContent(issueBody);
                }
            });
        trackSave(save);
    }

    const saveIssueInstructions = () => {

        let issueInstructions = '';

        if (editorInstructionRef.current) {
            issueInstructions = editorInstructionRef.current.getContent();
        }

        const save = fetch('/Archer3/SaveIssueInstructions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    issueId: props.issueId,
                    instructions: issueInstructions
                })
        }).then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveIssueInstructions',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getIssue();
                }
            });
        trackSave(save);
    }

    /*CLAUDE DRAFT (Special 999)*/
    const getPendingIssueDraft = async () => {
        let url = `/Archer3/GetPendingIssueDraft?issueId=${props.issueId}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetPendingIssueDraft',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setIssueDraft(result.result ?? null);
                }
            })
    }

    const acceptIssueDraft = async () => {
        if (!issueDraft) {
            return;
        }
        setDraftBusy(true);
        try {
            //Let any save already on its way (fired by the mouse leaving an editor) finish first
            await Promise.all(pendingSaves.current);

            const response = await fetch(`/Archer3/AcceptIssueDraft?issueDraftId=${issueDraft.issueDraftId}`, { method: 'POST' });
            const result = await response.json();
            if (result.isError) {
                let info = {
                    type: 'error',
                    title: 'The draft could not be accepted',
                    errorMessage: result.errorMessage,
                    secondaryErrorMessage: result.secondaryErrorMessage
                };
                setAlertInfo(info);
                setAlertInfoDialog(true);
                await getPendingIssueDraft();
            }
            else {
                setIssueDraft(null);
                setDraftPreviewOpen(false);
                await getIssue();
                //Remount the header input and both editors so they show the accepted text
                setEditorKey(key => key + 1);
            }
        }
        finally {
            setDraftBusy(false);
        }
    }

    const discardIssueDraft = async () => {
        if (!issueDraft) {
            return;
        }
        setDraftBusy(true);
        try {
            const response = await fetch(`/Archer3/DiscardIssueDraft?issueDraftId=${issueDraft.issueDraftId}`, { method: 'POST' });
            const result = await response.json();
            if (result.isError) {
                let info = {
                    type: 'error',
                    title: 'The draft could not be discarded',
                    errorMessage: result.errorMessage,
                    secondaryErrorMessage: result.secondaryErrorMessage
                };
                setAlertInfo(info);
                setAlertInfoDialog(true);
                await getPendingIssueDraft();
            }
            else {
                setIssueDraft(null);
                setDraftPreviewOpen(false);
            }
        }
        finally {
            setDraftBusy(false);
        }
    }

    const getIssuePreviewContent = () => {
        var params = [
            `issueId=${props.issueId}`,
            `letterId=${letterId}`
        ].join('&');

        let url = `/Archer3/GetIssuePreview?${params}`;

        fetch(url)
            .then(result => result.text())
            .then(result => {
                setPreivewContent(result);
            })
    }

    /*Flagged Items*/
    const [isFlaggedItemsEditorOpen, setFlaggedItemsEditorOpen] = useState(false);

    const openFlaggedItemsEditor = () => {
        setFlaggedItemsEditorOpen(true);
    };

    const closeFlaggedItemsEditor = () => {
        setFlaggedItemsEditorOpen(false);
        getIssue();
    };

    const removeFlaggedItem = (flaggedItemId: number) => {
        var params = [
            `flaggedItemId=${flaggedItemId}`,
        ].join('&');
        let url = `/Archer3/removeFlaggedItem?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RemoveFlaggedItem',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getIssue();
                }
            })
    }

    const updateFlaggedItems = () => {
        getIssue();
    }

    /*ATTACHMENTS*/
    const [isAttachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);

    const openAttachmentsDialog = () => {
        setAttachmentsDialogOpen(true);
    };

    const closeAttachmentsDialog = () => {
        setAttachmentsDialogOpen(false);
        getIssue();
    };

    const removeEnclosure = (enclosureId: number) => {
        let url = `/Archer3/RemoveEnclosure?enclosureId=${enclosureId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RemoveEnclosure',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getIssue();
                }
            })
    }

    /*NOTES*/
    const [issueNote, setIssueNote] = useState('');
    const [noteLabel, setNoteLabel] = useState('New Issue Note');
    const [noteId, setNoteId] = useState(0);
    const updateIssueNote = (event: any) => {
        setIssueNote(event.target.value);
    }

    const saveIssueNote = () => {
        var params = [
            `issueId=${props.issueId}`,
            `id=${noteId}`,
            `body=${encodeURIComponent(issueNote)}`
        ].join('&');
        let url = `/Archer3/SaveNote?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setIssueNote('');
                    getIssue();
                }
            })
    }

    const editIssueNote = (note: AuditNote) => {
        setNoteId(note.id);
        setNoteLabel('Edit Note...')
        setIssueNote(note.body);
    }

    const deleteNote = (noteId: number) => {
        let url = `/Archer3/DeleteNote?noteId=${noteId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DeleteNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    //maybe just remove it in UI?
                    getIssue();
                }
            })
    }


    /*DATA BINDING*/
    const [isDataBindingDialogOpen, setDataBindingDialogOpen] = useState(false);

    const openDataBindingDialog = () => {
        setDataBindingDialogOpen(true);
    };

    const closeDataBindingDialog = () => {
        setDataBindingDialogOpen(false);
    };

    const getDataBindingValue = (value: string) => {
        let valueDisplay = `{{${value}}}`
        if (editorBodyRef.current) {
            editorBodyRef.current.insertContent(valueDisplay);
        }
        setDataBindingDialogOpen(false);
    }

    /*BANK RECONCILATION*/
    const [isReconcilationDialogOpen, setReconcilationDialogOpen] = useState(false);

    const openReconcilationDialog = () => {
        setReconcilationDialogOpen(true);
    };

    const closeReconcilationDialog = () => {
        setReconcilationDialogOpen(false);
    };

    const getReconcilationHtml = (html: string) => {
        if (editorBodyRef.current) {
            editorBodyRef.current.insertContent(html);
        }
        setReconcilationDialogOpen(false);
    }


    /*PLACE HOLDER ITEM*/

    const [isPlaceHolderItemDialogOpen, setPlaceHolderItemDialogOpen] = useState(false);

    const openPlaceholderItemDialog = () => {
        setPlaceHolderItemDialogOpen(true);
    };

    const closePlaceHolderItemDialog = () => {
        setPlaceHolderItemDialogOpen(false);
        getIssue();
    };

    const deletePlaceholderItem = (flaggedItemId: number) => {
        let url = `/Archer3/deletePlaceHolderItem?id=${flaggedItemId}`;
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DeletePlaceholderItem',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getIssue();
                }
            })
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className="issue-editor-container">
            {issue &&
                <Grid container>
                    {/*Editor Container*/}
                    <Grid item xs={9.5}>
                    <Box sx={{ width: '100%' }}>
                        {/*Claude draft banner (Special 999)*/}
                        {
                            issueDraft &&
                            <Grid container className="issue-section">
                                <Grid item xs={12}>
                                    <Alert icon={false} severity="info" sx={{ fontSize: '1.1rem', '& .MuiAlert-message': { width: '100%' } }}>
                                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography sx={{ fontSize: '1.15rem' }}>
                                                <strong>Claude drafted this special</strong>
                                                {` (${issueDraft.draftedByName ? `${issueDraft.draftedByName}, ` : ''}${issueDraft.draftedDate}).`}
                                            </Typography>
                                            <Button size="large" variant="outlined" disabled={isDraftBusy} onClick={() => setDraftPreviewOpen(!isDraftPreviewOpen)}>
                                                {isDraftPreviewOpen ? 'Hide preview' : 'Preview'}
                                            </Button>
                                            <Button size="large" variant="contained" color="success" disabled={isDraftBusy} onClick={acceptIssueDraft}>
                                                Accept
                                            </Button>
                                            <Button size="large" variant="outlined" color="error" disabled={isDraftBusy} onClick={discardIssueDraft}>
                                                Discard
                                            </Button>
                                        </Stack>
                                        <Typography sx={{ fontSize: '1rem', marginTop: 1 }}>
                                            Accepting replaces the current header, body and instructions.
                                        </Typography>
                                        <Collapse in={isDraftPreviewOpen}>
                                            <Box sx={{ marginTop: 2 }}>
                                                <Typography variant="subtitle1" sx={{ color: blue[800] }}>Header</Typography>
                                                <Typography sx={{ fontSize: '1.1rem', marginBottom: 2 }}>{issueDraft.header}</Typography>
                                                <Typography variant="subtitle1" sx={{ color: blue[800] }}>Body</Typography>
                                                <Box component="section" sx={{ p: 2, marginBottom: 2, border: '1px dashed grey', backgroundColor: 'white' }}
                                                    dangerouslySetInnerHTML={{ __html: issueDraft.body }}>
                                                </Box>
                                                <Typography variant="subtitle1" sx={{ color: blue[800] }}>GATEWAY HELP (instructions)</Typography>
                                                <Box component="section" sx={{ p: 2, border: '1px dashed grey', backgroundColor: 'white' }}
                                                    dangerouslySetInnerHTML={{ __html: issueDraft.instructions }}>
                                                </Box>
                                            </Box>
                                        </Collapse>
                                    </Alert>
                                </Grid>
                            </Grid>
                        }
                        <Grid container className="issue-section">
                            <Grid item xs={8}>
                                <Stack direction="row" spacing={2} alignItems="left">
                                        <Typography variant="subtitle1" sx={{ color: blue[800], width: 150 }}>Header</Typography>
                                        <Input key={`header-${editorKey}`} defaultValue={issueHeader} sx={{ width: '80%' }} onChange={(event) => { saveIssueHeader(issue.issueId, event.target.value) }} />
                                </Stack>
                            </Grid>
                            <Grid item xs={4} justifyContent="flex-end"></Grid>
                        </Grid>
                        <Grid container className="issue-section">
                            <Grid item xs={6}>
                                <Stack direction="row" spacing={1} alignItems="left">
                                    <Typography variant="subtitle1" sx={{ color: blue[800], width: 150}}>Attachments</Typography>
                                        {/*display attachment list*/}
                                        {
                                            issue.enclosures.map((enclosure) =>
                                                <Chip className="is-clickable" label={enclosure.description} key={enclosure.enclosureId} variant="outlined" color="primary" onDelete={() => removeEnclosure(enclosure.enclosureId)} />
                                            )
                                        }
                                        <Chip className="is-clickable" label='Add' variant="outlined" color="success" onClick={openAttachmentsDialog} />
                                    </Stack>
                                    <IssueAttachmentsDialog issueId={issue.issueId} cpfId={issue.cpfId} isAttachmentsDialogOpen={isAttachmentsDialogOpen} closeAttachmentsDialog={closeAttachmentsDialog}></IssueAttachmentsDialog>
                                </Grid>
                            </Grid>
                            <Divider light sx={{ marginTop:1 }} />
                            <Grid container className="issue-section">
                                <Grid item xs={8} justifyContent="flex-start">
                                    <Typography variant="subtitle1" sx={{ color: blue[800], width: 150 }}>
                                        Flagged Items({flaggedItems && flaggedItems.length})
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} justifyContent="flex-end">
                                    <Stack direction="row" spacing={2}>
                                        <Chip className="is-clickable" label='Add a Customized Row' variant="outlined" color="info" onClick={openPlaceholderItemDialog} />
                                        <Chip className="is-clickable" label='Edit Items' variant="outlined" color="success" onClick={openFlaggedItemsEditor} />
                                    </Stack>
                                </Grid>
                                <FlaggedItemsEditor filerCpfId={issue.cpfId} issueId={issue.issueId} flaggedItems={flaggedItems} isFlaggedItemsEditorOpen={isFlaggedItemsEditorOpen} closeFlaggedItemsEditor={closeFlaggedItemsEditor}></FlaggedItemsEditor>
                                {/*Insert Placeholder Item*/}
                                <PlaceHolderItemDialog issueId={issue.issueId} isPlaceHolderItemDialogOpen={isPlaceHolderItemDialogOpen} closePlaceHolderItemDialog={closePlaceHolderItemDialog}></PlaceHolderItemDialog>
                            </Grid>
                            {
                                flaggedItems && issue.archerStatus === 'Draft' &&
                                <Grid container className="issue-section">
                                        <Grid item xs={12}>
                                            <FlaggedItemsTable flagItems={flaggedItems} removeFlaggedItem={removeFlaggedItem} updateFlaggedItems={updateFlaggedItems} isGroupUiEditable={issue.gatewayIssueCategoryId === excessContributionCategoryId} cpfId={issue.cpfId} auditPeriodId={issue.auditPeriodId}></FlaggedItemsTable>
                                    </Grid>
                                </Grid>
                            }
                            {
                                issue.placeholderItems.length > 0 &&
                                <Grid container className="issue-section">
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={2} alignItems="left">
                                            <Typography variant="subtitle1" sx={{color: blue[800]}}>Customized Rows({issue.placeholderItems.length})</Typography>
                                                {
                                                    issue.placeholderItems.map((item) =>
                                                        <Chip className="is-clickable" label={`Row ${item.ordinal}: ${item.groupUi} ${item.fullName} ${item.amount}`} key={item.flaggedItemId} variant="outlined" color="warning" onDelete={() => deletePlaceholderItem(item.flaggedItemId)} />
                                                    )
                                                }
                                        </Stack>
                                    </Grid>
                                </Grid>
                            }
                            <Divider light sx={{ marginTop: 1 }} />
                        <Grid container className="issue-section" sx={{ marginTop: 2 }}>
                            <Grid item xs={12}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs value={tabValue} onChange={switchTab}>
                                        <Tab label="Body" {...tabProps('body')} />
                                        <Tab label="Preview" {...tabProps('preview')} />
                                        <Tab label="Responses" {...tabProps('responses')} />
                                    </Tabs>
                                </Box>
                                <CustomTabPanel value={tabValue} id={'body'}>
                                    <Grid container className="text-editor-body-container">
                                        <Grid item xs={12}>
                                                <Editor
                                                    key={`body-${editorKey}`}
                                                    apiKey={editorAPIKey}
                                                    onInit={(evt: any, editor: any) => editorBodyRef.current = editor}
                                                    initialValue={issueContent}
                                                    init={{
                                                        height: 500,
                                                        menubar: true,
                                                        branding: false,
                                                        plugins: [
                                                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                            'insertdatetime', 'media', 'advtemplate', 'table', 'code', 'help', 'wordcount', 'tinymcespellchecker'
                                                        ],
                                                        contextmenu: 'advtemplate',
                                                        toolbar1: 'inserttemplate addtemplate undo redo | blocks | ' +
                                                            'bold italic underline forecolor fontsize | alignleft aligncenter ' +
                                                            'alignright alignjustify spellchecker | bullist numlist outdent indent | ' +
                                                            'removeformat | help | dataBinding | reconcilation',
                                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                                        fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
                                                        advtemplate_templates: props.issueTemplates,
                                                        setup: (editor:any) => {
                                                            editor.ui.registry.addButton('dataBinding', {
                                                                text: 'Insert Data Binding',
                                                                icon: 'sourcecode',
                                                                onAction: (_:any) => openDataBindingDialog()
                                                            });
                                                            editor.ui.registry.addButton('reconcilation', {
                                                                text: 'Insert Reconcilation',
                                                                icon: 'toc',
                                                                onAction: (_:any) => openReconcilationDialog()
                                                            });
                                                        }
                                                    }}
                                                    onMouseLeave={saveIssueBody}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container className="issue-section">
                                        <Grid item xs={12} sx={{ marginTop: 2 }}>
                                                <Typography variant="subtitle1" sx={{ color: blue[800]}}>GATEWAY HELP</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container className="text-editor-instruction-container">
                                            <Grid item xs={12}>
                                                <Editor
                                                    key={`instructions-${editorKey}`}
                                                    apiKey={editorAPIKey}
                                                    onInit={(evt: any, editor:any) => editorInstructionRef.current = editor}
                                                    initialValue={issueInstructions}
                                                    init={{
                                                        height: 500,
                                                        menubar: true,
                                                        branding: false,
                                                        plugins: [
                                                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount', 'tinymcespellchecker'
                                                        ],
                                                        toolbar: 'undo redo | blocks | ' +
                                                            'bold italic forecolor fontsize | alignleft aligncenter ' +
                                                            'alignright alignjustify spellchecker| bullist numlist outdent indent | ' +
                                                            'removeformat | help',
                                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                                        fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt"
                                                    }}
                                                    onMouseLeave={saveIssueInstructions}
                                                />
                                            </Grid>
                                        </Grid>
                                </CustomTabPanel>
                                <CustomTabPanel value={tabValue} id={'preview'}>
                                    <Box
                                        component="section"
                                        className="body-preview"
                                        sx={{
                                            p: 2,
                                            border: '1px dashed grey',
                                        }}
                                        >
                                        <Grid sx={{ m: 1 }} container dangerouslySetInnerHTML={{ __html: issuePreviewContent }}>
                                        </Grid>
                                        </Box>
                                        <Grid container className="issue-section">
                                            <Grid item xs={12} sx={{marginTop:2}}>
                                                <Typography variant="subtitle1" sx={{ color: blue[800] }}>GATEWAY HELP</Typography>
                                            </Grid>
                                        </Grid>
                                        <Box
                                            component="section"
                                            className="instruction-preview"
                                            sx={{
                                                p: 2,
                                                border: '1px dashed grey',
                                            }}
                                        >
                                            <Grid sx={{ marginLeft: 2 }} container dangerouslySetInnerHTML={{ __html: issueInstructions }}>
                                            </Grid>
                                        </Box>
                                    </CustomTabPanel>
                                    <CustomTabPanel value={tabValue} id={'responses'}>
                                        <FilerGatewayResponses cpfId={issue.cpfId} issue={issue}></FilerGatewayResponses>
                                </CustomTabPanel>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
                    {/*Issue Notes Container*/}
                    <Grid item xs={0.5}></Grid>
                    <Grid item xs={2} className="notes-container">
                    <Stack
                        direction="row" spacing={2}
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <Avatar className="small-avatar is-clickable" onClick={closeIssueEditor}>
                            <Close className="small-icon" />
                        </Avatar>
                    </Stack>
                    <Grid container sx={{ marginTop: 1 }}>
                        <Paper
                            component="form"
                            sx={{ p: 2 }}
                            >
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-end",
                                    }}>
                                    <TextField
                                        label={noteLabel}
                                        size="small"
                                        value={issueNote}
                                        onChange={updateIssueNote}
                                        focused
                                        multiline
                                        rows={2}
                                        maxRows={10}
                                    />
                                    <Chip label='save' size="small" variant="outlined" color="success" onClick={saveIssueNote} />
                                </Stack>
                            
                        </Paper>
                    </Grid>
                    {/*Notes List*/}
                    <Grid container spacing={2} sx={{ marginTop: 1 }}>
                        {
                            issue.notes.length > 0 &&
                            issue.notes.map((note) =>
                                <AuditNoteCard key={note.id} note={note} deleteNote={deleteNote} saveNote={() => editIssueNote(note)}></AuditNoteCard>
                            )
                        }
                    </Grid>
                    </Grid>
                    {/*Insert Bank Reconcilation*/}
                    <IssueReconcilationDialog cpfId={issue.cpfId} isReconcilationDialogOpen={isReconcilationDialogOpen} closeReconcilationDialog={closeReconcilationDialog} getReconcilationHtml={getReconcilationHtml}></IssueReconcilationDialog>
                    {/*Insert Data Binding*/}
                    <IssueDataBindingDialog isDataBindingDialogOpen={isDataBindingDialogOpen} closeDataBindingDialog={closeDataBindingDialog} getDataBindingValue={getDataBindingValue}></IssueDataBindingDialog>
                </Grid>
            }
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </div>
    )
}

export default ArcherIssueEditor